import { Script, type Script as TScript } from './schema';

/**
 * Map of legacy/script-author bg slugs → actual generated asset slugs.
 * Add entries here when ART-3 hasn't generated a specific bg the script names.
 */
const BG_SLUG_ALIAS: Record<string, string> = {
  // After 2026-05-05 AAA bg generation, all 23 slugs have unique art.
  // Only true duplicates (same physical room) remain aliased.
  'rook-office': 'rooks-office',          // simple typo alias
  'edit-suite': 'lethe-edit-room',        // same room, normal lighting
  'mira-apartment-dark': 'apartment-night', // same room, dark
  'server-room': 'lethe-archive',         // same physical room
};

/** Map of legacy portrait slugs → actual generated portrait slugs. */
const PORTRAIT_SLUG_ALIAS: Record<string, string> = {
  // Most names match — pass-through. Add entries here as needed.
};

function normalizeAssetPath(p: string, kind: 'bg' | 'portrait' | 'cg'): string {
  if (!p) return p;
  // 1. Add assets/ prefix if missing
  if (!p.startsWith('assets/') && !p.startsWith('./') && !p.startsWith('http')) {
    if (p.startsWith('bg/') || p.startsWith('portraits/') || p.startsWith('cg/')) {
      p = `assets/${p}`;
    }
  }
  // 2. Asset extension is preserved as authored. The on-disk asset format
  //    must match what the script references; do not silently rewrite here.
  //    (Earlier versions rewrote .png → .webp under the assumption that all
  //    generated assets were .webp, but real generated assets ship as .png.
  //    Rewriting here caused the dev server's SPA fallback to serve index.html
  //    instead of the image, producing blank backgrounds and portraits.)
  // 3. Apply slug alias for bg
  if (kind === 'bg') {
    const m = p.match(/^(assets\/bg\/)([a-z0-9-]+)(\.[a-z0-9]+)$/);
    if (m) {
      const [, dir, slug, ext] = m;
      const aliased = BG_SLUG_ALIAS[slug] ?? slug;
      p = `${dir}${aliased}${ext}`;
    }
  }
  if (kind === 'portrait') {
    const m = p.match(/^(assets\/portraits\/)([a-z0-9-]+)(\.[a-z0-9]+)$/);
    if (m) {
      const [, dir, slug, ext] = m;
      const aliased = PORTRAIT_SLUG_ALIAS[slug] ?? slug;
      p = `${dir}${aliased}${ext}`;
    }
  }
  return p;
}

function normalizeScript(script: TScript): TScript {
  for (const beat of script.beats) {
    const b = beat as Record<string, unknown>;
    if (typeof b.bg === 'string') b.bg = normalizeAssetPath(b.bg, 'bg');
    if (typeof b.portrait === 'string') b.portrait = normalizeAssetPath(b.portrait, 'portrait');
    if (typeof b.cg === 'string') b.cg = normalizeAssetPath(b.cg, 'cg');
  }
  return script;
}

export async function loadScript(url: string): Promise<TScript> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to load ${url}: ${res.status}`);
  const json: unknown = await res.json();
  const parsed = Script.parse(json);
  return normalizeScript(parsed);
}
